import { Toast } from "@twinkltech/curriculum-architect-component-library";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button, Text } from "@twinkltech/web-ui";
import { useState } from "react";

import { AppHeader } from "@/components/AppHeader/AppHeader";
import { SidebarNav } from "@/components/SidebarNav/SidebarNav";

import { paths } from "@/paths";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { Z_INDEX_ORDERS } from "../config/zIndexOrders.const";

const FEEDBACK_TOAST_FADE_OUT_DURATION_MS = 400;

export const MainLayout = ({
  canViewCurricula,
  children,
  isLoading,
}: {
  children?: React.ReactNode;
  canViewCurricula?: boolean;
  isLoading?: boolean;
}) => {
  const { click } = useTwinklAnalytics();
  const { feedbackState, onFeedbackEngaged, onFeedbackToastDismissed } =
    useFeedback();

  const [toastAnimationState, setToastAnimationState] = useState<
    "idle" | "fading-out" | "sliding-in"
  >("idle");

  // Determine animation state based on feedback state
  const currentAnimationState =
    feedbackState === "toast" && toastAnimationState === "idle"
      ? "sliding-in"
      : toastAnimationState;

  return (
    <div className="flex flex-col min-h-screen">
      <AppHeader />

      <div className="flex grow pl-12 py-6">
        <div className={Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.SIDE_MENU}>
          <SidebarNav
            canViewCurricula={canViewCurricula}
            isLoading={isLoading}
          />
        </div>
        <main className="grow-[1] w-full overflow-hidden">{children}</main>
      </div>
      {feedbackState === "toast" && (
        <Toast
          className="fixed bottom-4 right-4 z-50"
          data-tracking-impression='{"application": "curriculum-architect", "toast": "feedback-prompt", "position": "bottom-right"}'
          fadeOutActive={currentAnimationState === "fading-out"}
          fadeOutDurationMs={FEEDBACK_TOAST_FADE_OUT_DURATION_MS}
          handleClose={() => {
            click("cta_click", {
              ctaCopy: "cancel",
              clickLocation: "Feedback Toast",
            });
            setToastAnimationState("fading-out");
            setTimeout(() => {
              onFeedbackToastDismissed();
              setToastAnimationState("idle");
            }, FEEDBACK_TOAST_FADE_OUT_DURATION_MS);
          }}
          id="feedback-toast"
          slideInActive={currentAnimationState === "sliding-in"}
          type="default"
        >
          <div className="flex items-center gap-8">
            <Text>We’d love to hear what you think!</Text>
            <Button
              as={"a"}
              href={paths.external.FEEDBACK_SURVEY_URL}
              rel="noopener noreferrer"
              size="sm"
              target="_blank"
              variant="secondary"
              onClick={() => {
                click("click_cta", {
                  ctaCopy: "Leave Feedback",
                  clickLocation: "Feedback Toast",
                });
                onFeedbackEngaged();
              }}
            >
              Leave Feedback
            </Button>
          </div>
        </Toast>
      )}
    </div>
  );
};
